/**
 * Inspection Report View
 *
 * Generates a customer-friendly inspection report that can be:
 * - Printed directly
 * - Saved as PDF via browser print
 * - Sent to customer via WhatsApp/Email
 */

import { format } from 'date-fns';
import i18n from '@/lib/i18n';
import { conditionColors, chartColors } from '@/styles/theme';
import type { Inspection, InspectionItemCondition } from '@/types';
import { dateFnsLocale, formatNumber } from '@/utils/locale';
import { useNotificationStore } from '@/stores/notificationStore';
import { printHtmlWindow } from '@/lib/printWindow';

interface InspectionReportData {
  inspection: Inspection;
  workshopName?: string;
  workshopAddress?: string;
  workshopPhone?: string;
  workshopEmail?: string;
  workshopLogo?: string;
}

function getConditionLabels(): Record<InspectionItemCondition, string> {
  const t = (key: string) => i18n.t(key, { ns: 'inspection' });
  return {
    good: t('report.good'),
    fair: t('report.fair'),
    poor: t('report.poor'),
    critical: t('report.critical'),
  };
}

const conditionIcons: Record<InspectionItemCondition, string> = {
  good: '✓',
  fair: '!',
  poor: '⚠',
  critical: '✕',
};

// Print-only ink and paper-surface shades for the generated PDF/print stylesheet.
// These are deliberately fixed (black-ish ink, light paper tints) and are NOT
// part of the on-screen MUI theme — a printed report must look identical
// regardless of the tenant's light/dark UI palette. Centralized here so the
// HTML-string template below interpolates them instead of hardcoding hex.
const printColors = {
  ink: '#1a1a1a', // near-black body text
  inkStrong: '#333', // headings on paper
  inkMuted: '#666', // secondary text on paper
  inkFaint: '#999', // disabled / not-inspected text
  border: '#e0e0e0', // hairline borders/dividers
  surfaceLight: '#f8f9fa', // info-box background
  surfaceCategory: '#f0f0f0', // category heading background
  surfaceHead: '#fafafa', // table header background
  surfaceGrey: '#f5f5f5', // measurement-grid / rec-table header background
  measurementBg: '#e3f2fd', // measurement chip background
  issueBg: '#fef2f2', // flagged issue row background
  recBg: '#fff8e1', // recommendations section background
  recBorder: '#ffe082', // recommendations section border
  approvalBg: '#e8f5e9', // approval section background
  approvalBorder: '#4caf50', // approval section border
  approvalText: '#2e7d32', // approval heading text
} as const;

function generateReportHTML(data: InspectionReportData): string {
  const { inspection, workshopName, workshopAddress, workshopPhone, workshopEmail } = data;
  const t = (key: string) => i18n.t(key, { ns: 'inspection' });
  const conditionLabels = getConditionLabels();

  // Calculate statistics
  const allItems = inspection.categories.flatMap((cat) => cat.items);
  const goodItems = allItems.filter((item) => item.condition === 'good').length;
  const fairItems = allItems.filter((item) => item.condition === 'fair').length;
  const poorItems = allItems.filter((item) => item.condition === 'poor').length;
  const criticalItems = allItems.filter((item) => item.condition === 'critical').length;

  // Get all recommendations
  const recommendations = allItems
    .filter((item): item is typeof item & { recommendation: NonNullable<typeof item.recommendation> } =>
      item.recommendation !== undefined
    )
    .map((item) => ({
      itemName: item.name,
      categoryName: inspection.categories.find((c) => c.items.includes(item))?.name ?? '',
      ...item.recommendation,
    }));

  const immediateRecs = recommendations.filter((r) => r.urgency === 'immediate');
  const soonRecs = recommendations.filter((r) => r.urgency === 'soon');
  const futureRecs = recommendations.filter((r) => r.urgency === 'future');

  const formatPrice = (cents: number) => `€${(cents / 100).toFixed(2)}`;
  const formatDate = (dateStr: string) => format(new Date(dateStr), 'd.M.yyyy', { locale: dateFnsLocale() });

  // Generate category sections
  const categorySections = inspection.categories
    .map((category) => {
      return `
        <div class="category">
          <h3>${category.name}</h3>
          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 50%">${t('report.item')}</th>
                <th style="width: 25%">${t('report.condition')}</th>
                <th style="width: 25%">${t('report.remarks')}</th>
              </tr>
            </thead>
            <tbody>
              ${category.items
                .map(
                  (item) => `
                <tr class="${item.condition === 'poor' || item.condition === 'critical' ? 'issue-row' : ''}">
                  <td>
                    <strong>${item.name}</strong>
                    ${item.description ? `<br><small>${item.description}</small>` : ''}
                    ${item.hasMeasurement && item.measurementValue !== null && item.measurementValue !== undefined && !(item.measurementValues && item.measurementValues.length > 0) ? `<br><span class="measurement">${String(item.measurementValue)} ${item.measurementUnit ?? ''}</span>` : ''}
                    ${item.measurementValues && item.measurementValues.length > 0 && item.measurementsConfig ? (() => {
                      const subItems = item.subItemsConfig ?? [];
                      const measurements = item.measurementsConfig;
                      if (subItems.length === 0) {
                        return `<br><span class="measurement">${measurements.map((m: { id: string; label: string; unit: string }) => {
                          const entry = item.measurementValues?.find((v: { measurementId: string }) => v.measurementId === m.id);
                          return entry ? `${m.label}: ${entry.value} ${m.unit}` : '';
                        }).filter(Boolean).join(', ')}</span>`;
                      }
                      return `<br><table class="measurement-grid"><thead><tr><th></th>${measurements.map((m: { label: string; unit: string }) => `<th>${m.label} (${m.unit})</th>`).join('')}</tr></thead><tbody>${subItems.map((si: { id: string; label: string }) => `<tr><td><strong>${si.label}</strong></td>${measurements.map((m: { id: string }) => {
                        const entry = item.measurementValues?.find((v: { subItemId?: string; measurementId: string }) => v.measurementId === m.id && v.subItemId === si.id);
                        return `<td>${entry ? entry.value : '-'}</td>`;
                      }).join('')}</tr>`).join('')}</tbody></table>`;
                    })() : ''}
                  </td>
                  <td>
                    ${
                      item.condition
                        ? `<span class="condition-badge" style="background-color: ${conditionColors[item.condition]}20; color: ${conditionColors[item.condition]}; border: 1px solid ${conditionColors[item.condition]}">
                        ${conditionIcons[item.condition]} ${conditionLabels[item.condition]}
                      </span>`
                        : `<span class="not-inspected">${t('report.notInspected')}</span>`
                    }
                  </td>
                  <td>${item.notes ?? '-'}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `;
    })
    .join('');

  // Generate recommendations section
  const recommendationsSection =
    recommendations.length > 0
      ? `
    <div class="section recommendations-section">
      <h2>${t('report.recommendedActions')}</h2>

      ${
        immediateRecs.length > 0
          ? `
        <div class="rec-group urgent">
          <h3 style="color: ${conditionColors.critical};">${t('report.urgentActions')}</h3>
          <p class="rec-note">${t('report.urgentActionsNote')}</p>
          <table class="rec-table">
            <thead>
              <tr>
                <th>${t('report.action')}</th>
                <th style="text-align: right;">${t('report.estimatedPrice')}</th>
              </tr>
            </thead>
            <tbody>
              ${immediateRecs
                .map(
                  (r) => `
                <tr>
                  <td>
                    <strong>${r.description}</strong>
                    <br><small>${r.categoryName} - ${r.itemName}</small>
                  </td>
                  <td style="text-align: right; white-space: nowrap;">${formatPrice(r.estimatedPriceCents)}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `
          : ''
      }

      ${
        soonRecs.length > 0
          ? `
        <div class="rec-group soon">
          <h3 style="color: ${conditionColors.poor};">${t('report.recommendedSoon')}</h3>
          <p class="rec-note">${t('report.recommendedSoonNote')}</p>
          <table class="rec-table">
            <thead>
              <tr>
                <th>${t('report.action')}</th>
                <th style="text-align: right;">${t('report.estimatedPrice')}</th>
              </tr>
            </thead>
            <tbody>
              ${soonRecs
                .map(
                  (r) => `
                <tr>
                  <td>
                    <strong>${r.description}</strong>
                    <br><small>${r.categoryName} - ${r.itemName}</small>
                  </td>
                  <td style="text-align: right; white-space: nowrap;">${formatPrice(r.estimatedPriceCents)}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `
          : ''
      }

      ${
        futureRecs.length > 0
          ? `
        <div class="rec-group future">
          <h3 style="color: ${chartColors[0]};">${t('report.futureActions')}</h3>
          <p class="rec-note">${t('report.futureActionsNote')}</p>
          <table class="rec-table">
            <thead>
              <tr>
                <th>${t('report.action')}</th>
                <th style="text-align: right;">${t('report.estimatedPrice')}</th>
              </tr>
            </thead>
            <tbody>
              ${futureRecs
                .map(
                  (r) => `
                <tr>
                  <td>
                    <strong>${r.description}</strong>
                    <br><small>${r.categoryName} - ${r.itemName}</small>
                  </td>
                  <td style="text-align: right; white-space: nowrap;">${formatPrice(r.estimatedPriceCents)}</td>
                </tr>
              `
                )
                .join('')}
            </tbody>
          </table>
        </div>
      `
          : ''
      }

      <div class="total-section">
        <table class="total-table">
          <tr>
            <td><strong>${t('report.totalRecommendedPrice')}:</strong></td>
            <td style="text-align: right;"><strong class="total-price">${formatPrice(inspection.totalRecommendedCents)}</strong></td>
          </tr>
        </table>
      </div>
    </div>
  `
      : '';

  return `
    <!DOCTYPE html>
    <html lang="${i18n.language}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${t('report.vehicleInspection')} - ${inspection.vehicle.registrationNumber}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 11pt;
          line-height: 1.5;
          color: ${printColors.ink};
          padding: 20mm;
          max-width: 210mm;
          margin: 0 auto;
        }

        @media print {
          body {
            padding: 0;
          }
          .page-break {
            page-break-before: always;
          }
          .no-print {
            display: none;
          }
        }

        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 30px;
          padding-bottom: 20px;
          border-bottom: 2px solid ${chartColors[0]};
        }

        .workshop-info h1 {
          font-size: 18pt;
          color: ${chartColors[0]};
          margin-bottom: 5px;
        }

        .workshop-info p {
          font-size: 9pt;
          color: ${printColors.inkMuted};
        }

        .report-title {
          text-align: right;
        }

        .report-title h2 {
          font-size: 14pt;
          color: ${printColors.inkStrong};
          margin-bottom: 5px;
        }

        .report-title .date {
          font-size: 10pt;
          color: ${printColors.inkMuted};
        }

        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 30px;
        }

        .info-box {
          background: ${printColors.surfaceLight};
          padding: 15px;
          border-radius: 8px;
          border: 1px solid ${printColors.border};
        }

        .info-box h3 {
          font-size: 10pt;
          color: ${printColors.inkMuted};
          margin-bottom: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-box .value {
          font-size: 14pt;
          font-weight: 600;
          color: ${printColors.ink};
        }

        .info-box .subvalue {
          font-size: 10pt;
          color: ${printColors.inkMuted};
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 15px;
          margin-bottom: 30px;
        }

        .summary-item {
          text-align: center;
          padding: 15px;
          border-radius: 8px;
          border: 2px solid;
        }

        .summary-item.good { border-color: ${conditionColors.good}; background: ${conditionColors.good}10; }
        .summary-item.fair { border-color: ${conditionColors.fair}; background: ${conditionColors.fair}10; }
        .summary-item.poor { border-color: ${conditionColors.poor}; background: ${conditionColors.poor}10; }
        .summary-item.critical { border-color: ${conditionColors.critical}; background: ${conditionColors.critical}10; }

        .summary-item .count {
          font-size: 24pt;
          font-weight: 700;
        }

        .summary-item.good .count { color: ${conditionColors.good}; }
        .summary-item.fair .count { color: ${conditionColors.fair}; }
        .summary-item.poor .count { color: ${conditionColors.poor}; }
        .summary-item.critical .count { color: ${conditionColors.critical}; }

        .summary-item .label {
          font-size: 9pt;
          color: ${printColors.inkMuted};
          text-transform: uppercase;
        }

        .section {
          margin-bottom: 25px;
        }

        .section h2 {
          font-size: 14pt;
          color: ${chartColors[0]};
          margin-bottom: 15px;
          padding-bottom: 5px;
          border-bottom: 1px solid ${printColors.border};
        }

        .category {
          margin-bottom: 20px;
        }

        .category h3 {
          font-size: 11pt;
          color: ${printColors.inkStrong};
          margin-bottom: 10px;
          background: ${printColors.surfaceCategory};
          padding: 8px 12px;
          border-radius: 4px;
        }

        .items-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10pt;
        }

        .items-table th {
          background: ${printColors.surfaceHead};
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          border-bottom: 2px solid ${printColors.border};
        }

        .items-table td {
          padding: 8px 12px;
          border-bottom: 1px solid ${printColors.border};
          vertical-align: top;
        }

        .items-table .issue-row {
          background: ${printColors.issueBg};
        }

        .condition-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 9pt;
          font-weight: 500;
        }

        .not-inspected {
          color: ${printColors.inkFaint};
          font-style: italic;
        }

        .measurement {
          display: inline-block;
          background: ${printColors.measurementBg};
          color: ${chartColors[0]};
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 9pt;
          font-weight: 500;
        }

        .measurement-grid {
          width: 100%;
          border-collapse: collapse;
          font-size: 8pt;
          margin-top: 4px;
        }
        .measurement-grid th, .measurement-grid td {
          border: 1px solid ${printColors.border};
          padding: 3px 6px;
          text-align: center;
        }
        .measurement-grid th {
          background: ${printColors.surfaceGrey};
          font-weight: 600;
        }
        .measurement-grid td:first-child {
          text-align: left;
        }

        .recommendations-section {
          background: ${printColors.recBg};
          padding: 20px;
          border-radius: 8px;
          border: 1px solid ${printColors.recBorder};
        }

        .rec-group {
          margin-bottom: 20px;
        }

        .rec-group h3 {
          font-size: 11pt;
          margin-bottom: 5px;
        }

        .rec-note {
          font-size: 9pt;
          color: ${printColors.inkMuted};
          margin-bottom: 10px;
        }

        .rec-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 10pt;
          background: white;
          border-radius: 4px;
          overflow: hidden;
        }

        .rec-table th {
          background: ${printColors.surfaceGrey};
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
        }

        .rec-table td {
          padding: 10px 12px;
          border-bottom: 1px solid ${printColors.border};
        }

        .total-section {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 2px solid ${printColors.recBorder};
        }

        .total-table {
          width: 100%;
        }

        .total-price {
          font-size: 16pt;
          color: ${chartColors[0]};
        }

        .footer {
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid ${printColors.border};
          text-align: center;
          font-size: 9pt;
          color: ${printColors.inkMuted};
        }

        .approval-section {
          margin-top: 30px;
          padding: 20px;
          background: ${printColors.approvalBg};
          border: 2px solid ${printColors.approvalBorder};
          border-radius: 8px;
        }

        .approval-section h3 {
          color: ${printColors.approvalText};
          margin-bottom: 10px;
        }

        .signature-line {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
        }

        .signature-box {
          width: 45%;
        }

        .signature-box .line {
          border-bottom: 1px solid ${printColors.inkStrong};
          margin-bottom: 5px;
          height: 40px;
        }

        .signature-box .label {
          font-size: 9pt;
          color: ${printColors.inkMuted};
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="workshop-info">
          <h1>${workshopName ?? ''}</h1>
          ${workshopAddress ? `<p>${workshopAddress}</p>` : ''}
          ${workshopPhone || workshopEmail ? `<p>${[workshopPhone, workshopEmail].filter(Boolean).join(' | ')}</p>` : ''}
        </div>
        <div class="report-title">
          <h2>${t('report.vehicleInspection')}</h2>
          <div class="date">${formatDate(inspection.completedAt ?? inspection.createdAt)}</div>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-box">
          <h3>${t('report.vehicle')}</h3>
          <div class="value">${inspection.vehicle.registrationNumber}</div>
          <div class="subvalue">${inspection.vehicle.make} ${inspection.vehicle.model} (${String(inspection.vehicle.year)})</div>
          <div class="subvalue">${t('report.mileage')}: ${formatNumber(inspection.mileage)} km</div>
        </div>
        <div class="info-box">
          <h3>${t('report.customer')}</h3>
          <div class="value">${inspection.customer.firstName} ${inspection.customer.lastName}</div>
          <div class="subvalue">${inspection.customer.phone}</div>
        </div>
      </div>

      <div class="section">
        <h2>${t('report.inspectionSummary')}</h2>
        <div class="summary-grid">
          <div class="summary-item good">
            <div class="count">${String(goodItems)}</div>
            <div class="label">${t('report.good')}</div>
          </div>
          <div class="summary-item fair">
            <div class="count">${String(fairItems)}</div>
            <div class="label">${t('report.fair')}</div>
          </div>
          <div class="summary-item poor">
            <div class="count">${String(poorItems)}</div>
            <div class="label">${t('report.poor')}</div>
          </div>
          <div class="summary-item critical">
            <div class="count">${String(criticalItems)}</div>
            <div class="label">${t('report.critical')}</div>
          </div>
        </div>
      </div>

      ${recommendationsSection}

      <div class="section page-break">
        <h2>${t('report.detailedReport')}</h2>
        ${categorySections}
      </div>

      ${
        recommendations.length > 0
          ? `
        <div class="approval-section no-print">
          <h3>${t('report.approveRecommendations')}</h3>
          <p>${t('report.approveRecommendationsNote')}</p>
          <div class="signature-line">
            <div class="signature-box">
              <div class="line"></div>
              <div class="label">${t('report.customerSignature')}</div>
            </div>
            <div class="signature-box">
              <div class="line"></div>
              <div class="label">${t('report.date')}</div>
            </div>
          </div>
        </div>
      `
          : ''
      }

      <div class="footer">
        <p>${t('report.reportCreated')} ${format(new Date(), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}</p>
        ${workshopName ? `<p>${workshopName} - ${t('report.allRightsReserved')}</p>` : ''}
      </div>
    </body>
    </html>
  `;
}

/**
 * Generate and display inspection report in a new window for printing
 */
export function generateInspectionReport(data: InspectionReportData): void {
  const html = generateReportHTML(data);

  void printHtmlWindow(html, { features: 'width=900,height=700' }).then((result) => {
    if (result.status === 'popup-blocked') {
      // Not alert(): this is the app's own notification, and a native modal
      // here blocks the page the user was trying to print from (#2726).
      // getState() because this is a plain function, not a component.
      useNotificationStore.getState().addNotification({
        type: 'warning',
        title: i18n.t('report.popupBlocked', { ns: 'inspection' }),
      });
    }
  });
}

/**
 * Get report HTML for sending via API (e.g., for PDF generation on server)
 */
export function getInspectionReportHTML(data: InspectionReportData): string {
  return generateReportHTML(data);
}
